import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import React, { useEffect, useState } from "react";
import { Button, Divider, Input, Space } from "antd";
import { ArrowRightOutlined, CopyOutlined } from "@ant-design/icons";

import { ContentWidth } from "../../../lib/ui-components/layout/content-width";
import { useEmsApi } from "../../../lib/contexts/api";
import { LoadingScreen } from "../../../lib/ui-components/loading";
import { Heading1, Heading2, Heading3 } from "../../../styles/typography";
import { useUser } from "../../../lib/contexts/user";
import { ApiClient } from "../../../lib/api-client";
import { Box } from "../../../lib/box";
import { useAsyncError } from "../../../lib/async";
import { AgentNotAvailable } from "../fallback/monitoring-fallback";

type ActionCardProps = {
  tagline: string;
  headline: string;
  description: React.ReactNode;
  action: React.ReactNode;
};

function ActionCard({ tagline, headline, description, action }: ActionCardProps) {
  return (
    <Box className="pt-10 pb-16 px-16 mb-8">
      <div className="text-secondary text-black font-bold mb-2">{tagline}</div>
      <div className="mb-10">
        <Heading2>{headline}</Heading2>
      </div>
      <div className="mb-10">{description}</div>
      {action}
    </Box>
  );
}

type AgentFunctionsProps = {
  linkToAgent: string;
  api: ApiClient;
};

function AgentFunctions({ linkToAgent, api }: AgentFunctionsProps) {
  const [newPassword, setNewPassword] = useState<string | undefined>(undefined);
  const [passwordGenerationInProgress, setPasswordGenerationInProgress] = useState(false);
  const [noAgentAccountError, setNoAgentAccountError] = useState(false);
  const { user } = useUser();

  async function getNewPassword() {
    setPasswordGenerationInProgress(true);
    try {
      const response = await api.getNewAgentPassword();
      setNewPassword(response.password);
    } catch (e: any) {
      if (e.status === 403) {
        setNoAgentAccountError(true);
      }
    } finally {
      setPasswordGenerationInProgress(false);
    }
  }

  return (
    <div>
      <Heading1 className="italic mt-16 mb-2">Enit agent</Heading1>
      <div className="grid grid-cols-2 items-start gap-x-16 mt-20">
        <ActionCard
          tagline={t`Energy Monitoring`}
          headline="agent Login"
          description={
            <Trans>
              This link will take you to the login of your energy monitoring software Enit agent. If
              you are outside of your organization network, this may involve a multi-step login
              process. Simply use your <b>login data of the energy monitoring software</b> for this
              purpose.
            </Trans>
          }
          action={
            <Button
              icon={<ArrowRightOutlined className="pr-2" />}
              type="primary"
              size="large"
              href={linkToAgent}
              target="_blank"
            >
              <Trans>Login to agent</Trans>
            </Button>
          }
        />
        <ActionCard
          tagline={t`Forgot password?`}
          headline={t`Reset password`}
          description={
            <Trans>
              If you forgot your <b>password for your energy monitoring software login</b>, you can
              reset your password here and have a new password generated. Make a note of the
              password or copy it by clicking on the icon behind the newly generated password.
            </Trans>
          }
          action={
            <div>
              <Button
                type="primary"
                size="large"
                onClick={() => {
                  getNewPassword();
                }}
                loading={passwordGenerationInProgress}
                className="w-72"
              >
                <Trans>Generate new password</Trans>
              </Button>
              {newPassword && (
                <div>
                  <Divider className="mt-6 mb-10" />
                  <div className="mt-6">
                    <Trans>
                      A new password was generated for the account <b>{user?.email}</b>.
                    </Trans>
                  </div>
                  <Space.Compact className="flex flex-row mt-2 w-72">
                    <Input
                      readOnly
                      disabled
                      value={newPassword}
                      aria-label="new Password"
                      className="!cursor-text"
                    />
                    <Button
                      title={t`Copy password`}
                      icon={<CopyOutlined />}
                      onClick={() => {
                        navigator.clipboard.writeText(newPassword);
                      }}
                    />
                  </Space.Compact>
                </div>
              )}
              {noAgentAccountError && (
                <div>
                  <Divider className="mt-6 mb-10" />
                  <div className="text-red-400 mt-6">
                    <Heading3>
                      <Trans>Password reset failed</Trans>
                    </Heading3>
                  </div>
                  <div className="mt-2">
                    <Trans>
                      There is no account <b>{user?.email}</b> on the connected Enit agent.
                    </Trans>
                  </div>
                </div>
              )}
            </div>
          }
        />
      </div>
    </div>
  );
}

export function Agent() {
  const api = useEmsApi();
  const [linkToAgent, setLinkToAgent] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const asyncError = useAsyncError();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const response = await api.getLinkToAgent();
        setLinkToAgent(response.location);
      } catch (e: any) {
        if (e.status === 404) {
          setLinkToAgent(null);
        } else {
          asyncError(new Error("Failed to load agent link."));
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [api, asyncError]);

  if (loading) {
    return <LoadingScreen wScreenDisabled={true} />;
  }

  return (
    <>
      <ContentWidth>
        {linkToAgent ? (
          <AgentFunctions linkToAgent={linkToAgent} api={api} />
        ) : (
          <AgentNotAvailable />
        )}
      </ContentWidth>
    </>
  );
}

export default Agent;
